import { useState } from "react";
import { Link } from "react-router-dom";

import {
  AlertHorizon,
  AlertRule,
  AlertSide,
  HORIZON_LABEL,
  HORIZON_ORDER,
  useAlertRules,
  useMyPositions,
  useUpdateAlertRule,
} from "../api/alerts";

const SIDE_LABEL: Record<AlertSide, string> = {
  bullish: "Buy alerts",
  bearish: "Sell-short alerts",
};

const INTRADAY_TOOLTIP =
  "Intraday rules fire on the same 30-minute rescore cadence as every other " +
  "horizon, on top of 5–15 min delayed Yahoo data. Treat them as informational, " +
  "not actionable.";

export default function AlertRulesPanel() {
  const rules = useAlertRules();
  const openPositions = useMyPositions({ status: "open", limit: 1 });

  if (rules.isLoading) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        Loading alert rules…
      </div>
    );
  }
  if (rules.isError || !rules.data) {
    return (
      <div className="rounded-lg border border-rose-900 bg-rose-950/30 p-4 text-sm text-rose-300">
        Couldn&apos;t load alert rules.
      </div>
    );
  }

  // Group by horizon. The API already returns them in canonical order, but
  // bucket explicitly so the layout doesn't depend on ordering.
  const byHorizon: Record<AlertHorizon, AlertRule[]> = {
    intraday: [], short: [], medium: [], long: [],
  };
  for (const r of rules.data) byHorizon[r.horizon].push(r);

  const nOpen = openPositions.data?.length ?? 0;

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Get a Telegram alert when a watchlist symbol&apos;s score enters a band
        you care about, and another when it exits. Each pair shows up under
        Hindsight → Strategy after it closes.
      </p>

      {HORIZON_ORDER.map((h) => (
        <div
          key={h}
          className="rounded-lg border border-slate-800 bg-slate-900/30 p-3"
        >
          <div className="mb-2 flex items-center gap-2">
            <div className="font-medium text-slate-200">{HORIZON_LABEL[h]}</div>
            {h === "intraday" && (
              <span
                title={INTRADAY_TOOLTIP}
                className="cursor-help rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300"
              >
                delayed ≤ 30 min ⓘ
              </span>
            )}
          </div>

          <div className="space-y-2">
            {byHorizon[h].map((rule) => (
              <RuleRow key={`${rule.horizon}-${rule.side}`} rule={rule} />
            ))}
          </div>
        </div>
      ))}

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-400">
        Open positions:{" "}
        <Link
          to="/hindsight?tab=strategy&status=open"
          className="text-sky-400 underline-offset-2 hover:underline"
        >
          {nOpen}
        </Link>
      </div>
    </div>
  );
}

function RuleRow({ rule }: { rule: AlertRule }) {
  const update = useUpdateAlertRule();
  const [editing, setEditing] = useState(false);

  const [floor, setFloor] = useState(rule.entry_floor);
  const [ceiling, setCeiling] = useState(rule.entry_ceiling);
  const [exit, setExit] = useState(rule.exit_threshold);
  const [err, setErr] = useState<string | null>(null);

  function toggle() {
    update.mutate({
      horizon: rule.horizon,
      side: rule.side,
      patch: { enabled: !rule.enabled },
    });
  }

  function save() {
    setErr(null);
    update.mutate(
      {
        horizon: rule.horizon,
        side: rule.side,
        patch: {
          entry_floor: floor,
          entry_ceiling: ceiling,
          exit_threshold: exit,
        },
      },
      {
        onSuccess: () => setEditing(false),
        onError: (e: Error & { response?: { data?: { detail?: string } } }) => {
          setErr(e?.response?.data?.detail || e.message);
        },
      },
    );
  }

  function cancel() {
    setFloor(rule.entry_floor);
    setCeiling(rule.entry_ceiling);
    setExit(rule.exit_threshold);
    setErr(null);
    setEditing(false);
  }

  const opLabel = rule.side === "bullish" ? "exit <" : "exit >";

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={rule.enabled}
          onChange={toggle}
          disabled={update.isPending}
          className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-sky-500 focus:ring-sky-500"
          aria-label={`Enable ${SIDE_LABEL[rule.side]}`}
        />
        <span className="text-slate-200">{SIDE_LABEL[rule.side]}</span>
      </label>

      {!editing ? (
        <>
          <span className="text-xs text-slate-400">
            enter {rule.entry_floor}–{rule.entry_ceiling}, {opLabel} {rule.exit_threshold}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="ml-auto text-xs text-sky-400 underline-offset-2 hover:underline"
          >
            edit
          </button>
        </>
      ) : (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <ThresholdInput label="floor" value={floor} onChange={setFloor} />
          <ThresholdInput label="ceiling" value={ceiling} onChange={setCeiling} />
          <ThresholdInput label="exit" value={exit} onChange={setExit} />
          <button
            onClick={save}
            disabled={update.isPending}
            className="rounded-md bg-sky-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={cancel}
            disabled={update.isPending}
            className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      )}
      {err && <span className="ml-auto text-xs text-rose-300">{err}</span>}
    </div>
  );
}

function ThresholdInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-xs text-slate-400">
      {label}
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-16 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200 focus:border-sky-500 focus:outline-none"
      />
    </label>
  );
}
